import { useState, useCallback } from 'react';
import { giftCardsApi, GiftCard, CreateGiftCardData } from '../../../services/api';

export function useGiftCards() {
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  const loadGiftCards = useCallback(async (
    page: number = 1,
    limit: number = 50,
    search?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ) => {
    try {
      setLoading(true);
      setError(null);
      const data = await giftCardsApi.getAll(undefined, page, limit, search, status, sortBy, sortOrder);
      setGiftCards(data.giftCards);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при загрузке сертификатов');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createGiftCard = useCallback(async (data: CreateGiftCardData) => {
    const quantity = data.quantity || 1;
    
    // Оптимистичное обновление
    const optimisticCards: GiftCard[] = Array.from({ length: quantity }, (_, i) => ({
      id: `temp-${Date.now()}-${i}`,
      name: data.name || '...',
      initialBalance: data.initialBalance.toString(),
      currentBalance: data.initialBalance.toString(),
      status: 'CAN_BE_USED' as const,
      expireDate: data.expireDate || null,
      retailStoreId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    setGiftCards(prev => [...optimisticCards, ...prev]);

    try {
      setError(null);
      const result = await giftCardsApi.create(data);
      
      // Если создано несколько сертификатов, result будет объектом с giftCards
      if (quantity > 1 && 'giftCards' in result) {
        // Удаляем оптимистичные элементы и добавляем реальные
        setGiftCards(prev => {
          const filtered = prev.filter(card => !card.id.startsWith('temp-'));
          return [...result.giftCards, ...filtered];
        });
      } else {
        // Один сертификат - заменяем оптимистичный элемент на реальный
        const created = result as GiftCard;
        setGiftCards(prev => prev.map(card => 
          card.id === optimisticCards[0].id ? created : card
        ));
      }
      
      // Перезагружаем для получения актуальных данных
      await loadGiftCards(pagination.page, pagination.limit);
    } catch (err: any) {
      // Откатываем оптимистичное обновление
      setGiftCards(prev => prev.filter(card => !card.id.startsWith('temp-')));
      const errorMessage = err.response?.data?.error || 'Ошибка при создании сертификата';
      setError(errorMessage);
      throw err;
    }
  }, [loadGiftCards, pagination]);

  const updateGiftCard = useCallback(async (id: string, data: { name?: string; expireDate?: string | null; status?: string; currentBalance?: number }) => {
    // Оптимистичное обновление
    const previousCards = [...giftCards];
    setGiftCards(prev => prev.map(card => {
      if (card.id === id) {
        return {
          ...card,
          ...(data.name !== undefined && { name: data.name }),
          ...(data.expireDate !== undefined && { expireDate: data.expireDate }),
          ...(data.status !== undefined && { status: data.status as GiftCard['status'] }),
          ...(data.currentBalance !== undefined && { currentBalance: data.currentBalance.toString() }),
          updatedAt: new Date().toISOString(),
        };
      }
      return card;
    }));

    try {
      setError(null);
      const updated = await giftCardsApi.update(id, data);
      // Заменяем оптимистичный элемент на реальный
      setGiftCards(prev => prev.map(card => 
        card.id === id ? updated : card
      ));
    } catch (err: any) {
      // Откатываем оптимистичное обновление
      setGiftCards(previousCards);
      const errorMessage = err.response?.data?.error || 'Ошибка при обновлении сертификата';
      setError(errorMessage);
      throw err;
    }
  }, [giftCards]);

  const deleteGiftCard = useCallback(async (id: string) => {
    // Оптимистичное обновление
    const previousCards = [...giftCards];
    setGiftCards(prev => prev.filter(card => card.id !== id));

    try {
      setError(null);
      await giftCardsApi.delete(id);
      // НЕ перезагружаем список - элемент уже удален оптимистично
      // Кэш на backend будет инвалидирован автоматически при удалении
    } catch (err: any) {
      // Откатываем оптимистичное обновление
      setGiftCards(previousCards);
      const errorMessage = err.response?.data?.error || 'Ошибка при удалении сертификата';
      setError(errorMessage);
      throw err;
    }
  }, [giftCards]);

  const getStatusLabel = useCallback((status: string): string => {
    const labels: Record<string, string> = {
      CAN_BE_USED: 'Не использован',
      PARTIALLY_REDEEMED: 'Частично использован',
      EXPIRED: 'Истек срок действия',
      REDEEMED: 'Полностью использован',
      INVALID: 'Недействителен',
    };
    return labels[status] || status;
  }, []);

  const getStatusClass = useCallback((status: string): string => {
    const classes: Record<string, string> = {
      CAN_BE_USED: 'status-available',
      PARTIALLY_REDEEMED: 'status-partial',
      EXPIRED: 'status-expired',
      REDEEMED: 'status-redeemed',
      INVALID: 'status-invalid',
    };
    return classes[status] || '';
  }, []);

  return {
    giftCards,
    loading,
    error,
    pagination,
    loadGiftCards,
    createGiftCard,
    updateGiftCard,
    deleteGiftCard,
    getStatusLabel,
    getStatusClass,
  };
}

